import {
  AlertCircle,
  Calendar,
  DollarSign,
  FileText,
  TrendingUp,
  Users,
  BarChart3,
  Package,
  Clock,
  CreditCard,
  X,
} from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { decodeJWTPayload } from '../../utils/jwt';

import { AlertCard } from '../../components/dashboard/AlertCard';
import { MetricCard } from '../../components/dashboard/MetricCard';
import { OnboardingDashboard } from '../../components/dashboard/OnboardingDashboard';
import { QuickActionCard, QuickAction } from '../../components/dashboard/QuickActionCard';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useDashboardAlerts } from '../../hooks/useDashboardAlerts';
import { useDashboardMetrics } from '../../hooks/useDashboardMetrics';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useBillingFeatureToggle } from '../../hooks/useBillingFeatureToggle';
import { useSaaSSubscription } from '../../hooks/useSaaSSubscription';
import { supabase } from '../../lib/supabase';
import { useImpersonationSession, useEndImpersonation } from '../../store/impersonation.store';
import { hasIncompleteOnboarding, getOnboardingUrl } from '../../utils/onboardingCheck';

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [accessDeniedError, setAccessDeniedError] = useState(false);
  const impersonationSession = useImpersonationSession();
  const endImpersonation = useEndImpersonation();
  const { data: metrics, isLoading: metricsLoading } = useDashboardMetrics();
  const { data: alerts = [], isLoading: alertsLoading } = useDashboardAlerts();
  const { role, allCompanies, isLoading: isLoadingCompany } = useCurrentCompany();
  const { data: billingEnabled, isLoading: isLoadingBillingToggle } = useBillingFeatureToggle();
  const { data: saasSubscription, isPending: isSubscriptionPending } = useSaaSSubscription();

  // Check if company is on trial
  const isOnTrial = Boolean(saasSubscription?.status === 'trialing' && saasSubscription?.trial_end_date);
  const trialDaysRemaining = isOnTrial && saasSubscription?.trial_end_date
    ? Math.max(0, Math.ceil((new Date(saasSubscription.trial_end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))
    : 0;
  
  // Show subscription signup card if user is owner/admin AND:
  // 1. No subscription exists (always show to prompt signup), OR
  // 2. On trial (show trial countdown regardless of billing status)
  const isOwnerOrAdmin = role === 'owner' || role === 'admin';
  const hasNoSubscription = !isSubscriptionPending && saasSubscription === null;
  // Only show card when we know the role (not loading) and user is owner/admin
  const shouldShowSubscriptionCard = !isLoadingCompany && isOwnerOrAdmin && (hasNoSubscription || isOnTrial);

  // Check for incomplete onboarding state - redirect to onboarding if found
  useEffect(() => {
    if (hasRedirectedToOnboardingRef.current) return;
    if (hasIncompleteOnboarding()) {
      hasRedirectedToOnboardingRef.current = true;
      const onboardingUrl = getOnboardingUrl();
      navigate(onboardingUrl);
      return;
    }
  }, [navigate]);

  // Check for access denied error from query params
  useEffect(() => {
    if (searchParams.get('error') === 'access_denied') {
      // If user is a student (non-owner/admin), redirect to student landing page
      if (role && !['owner', 'admin'].includes(role)) {
        navigate('/student-landing');
        return;
      }
      // Otherwise, show error message for owner/admin roles (shouldn't happen, but handle gracefully)
      setAccessDeniedError(true);
      // Remove error param from URL after displaying
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, role, navigate]);

  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/signin');
        return;
      }
      setLoading(false);
    };

    getUser();
  }, [navigate]);

  // Track if onboarding redirect has already fired to prevent re-triggering on re-renders
  const hasRedirectedToOnboardingRef = useRef(false);

  // Track if we've already checked to prevent infinite loops
  const hasCheckedSelectorRef = useRef(false);

  // Check if user should be redirected to company selector
  useEffect(() => {
    // Prevent multiple checks
    if (hasCheckedSelectorRef.current) return;
    
    const checkCompanySelector = async () => {
      if (isLoadingCompany || loading) return;

      // If user has multiple companies, show selector
      if (allCompanies.length > 1) {
        hasCheckedSelectorRef.current = true;
        try {
          // Check if user is a system admin (should always show selector)
          // This requires a valid JWT token - if parsing fails, we can't verify admin status
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError || !session?.access_token) {
            // No valid session - redirect to signin (RLS won't work without valid JWT)
            navigate('/signin');
            return;
          }

          // Parse JWT token to check admin status
          // If parsing fails, we can't proceed - RLS policies require valid JWT
          const claims = session.access_token ? decodeJWTPayload(session.access_token) : null;
          if (!claims) {
            // JWT parsing failed - cannot verify admin status without valid JWT
            // RLS policies won't work, so we must redirect to signin
            navigate('/signin');
            return;
          }
          const isSystemAdmin = claims.is_admin === true;
          
          // If system admin OR current role is not owner/admin, show selector
          // Note: role check here is safe because useCurrentCompany already validated JWT via RLS
          // Students will be routed to app download page from company selector
          if (isSystemAdmin || (role && !['owner', 'admin'].includes(role))) {
            navigate('/company-selector');
            return;
          }
        } catch {
          // Handle any unexpected errors - redirect to signin for security
          hasCheckedSelectorRef.current = false; // Reset on error
          navigate('/signin');
        }
      }
    };

    checkCompanySelector();
  }, [allCompanies.length, role, isLoadingCompany, loading, navigate]);

  const handleEndImpersonation = async () => {
    if (!impersonationSession) return;

    try {
      // Mark impersonation session as inactive in the database
      await supabase
        .from('impersonation_sessions')
        .update({ is_active: false, ended_at: new Date().toISOString() })
        .eq('id', impersonationSession.sessionId);

      // Clear the session from the store
      endImpersonation();

      // Navigate back to developer dashboard
      navigate('/dev/dashboard');
    } catch {
      // Error ending impersonation - already navigated or cleared
    }
  };

  const quickActions: QuickAction[] = [
    {
      id: 'students',
      label: 'Students',
      icon: Users,
      path: '/students',
      description: 'Manage members',
      isAvailable: true,
    },
    {
      id: 'subscriptions',
      label: 'Subscriptions',
      icon: Package,
      path: '/subscriptions-plans/gym',
      description: 'Manage plans',
      isAvailable: false,
    },
    {
      id: 'schedule',
      label: 'Schedule',
      icon: Calendar,
      path: '/schedule',
      description: 'View calendar',
      isAvailable: false,
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      path: '/analytics',
      description: 'View reports',
      isAvailable: false,
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: CreditCard,
      path: '/billing',
      description: 'Payment settings',
      isAvailable: true,
    },
    {
      id: 'waivers',
      label: 'Waivers',
      icon: FileText,
      path: '/damage-waivers',
      description: 'Manage waivers',
      isAvailable: true,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter'] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421] mx-auto" />
          <p className="mt-2 text-white/80">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Access Denied Error Banner */}
        {accessDeniedError && (
          <div className="mb-6 bg-red-900/20 border-2 border-red-500 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
              <div>
                <h3 className="text-red-500 font-bold text-lg">Access Denied</h3>
                <p className="text-red-100 text-sm">
                  You do not have the required permissions (owner or admin) to access that page.
                </p>
                <p className="text-red-100/70 text-xs mt-1">
                  If you believe this is an error, please contact your gym administrator.
                </p>
              </div>
              <button
                onClick={() => setAccessDeniedError(false)}
                className="ml-auto text-red-300 hover:text-red-100"
                aria-label="Dismiss error"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
        
        {/* Impersonation Banner */}
        {impersonationSession && (
          <div className="mb-6 bg-yellow-900/20 border-2 border-yellow-500 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-500" />
                <div>
                  <h3 className="text-yellow-500 font-bold text-lg">Developer Impersonation Mode</h3>
                  <p className="text-yellow-100 text-sm">
                    Viewing as: <strong>{impersonationSession.impersonatedUserEmail}</strong>
                  </p>
                  <p className="text-yellow-100/70 text-xs mt-1">
                    Reason: {impersonationSession.reason}
                  </p>
                </div>
              </div>
              <button
                onClick={handleEndImpersonation}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded transition-colors"
                data-testid="dashboard-end-impersonation-button"
              >
                <X className="w-4 h-4" />
                End Impersonation
              </button>
            </div>
          </div>
        )}

        {/* Header - matches Billing & Subscriptions page styling */}
        <div className="border-b border-white/10 bg-[#111010] pb-3 md:pb-4 mb-6">
          <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold">Dashboard</h1>
          <p className="text-white/80 mt-1">Welcome back! Here&apos;s what&apos;s happening at your gym.</p>
        </div>

        {/* Subscription Signup Reminder - Show when no subscription or on trial */}
        {!isLoadingCompany && !isSubscriptionPending && shouldShowSubscriptionCard && (
          <Card className="bg-[#221F1F] border-[#504A4A] mb-6 border-blue-600/40">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <div className="text-blue-400 mt-0.5">
                  <Clock className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-[#EBFFEE] font-semibold mb-1">
                    {hasNoSubscription 
                      ? 'Sign Up for Subscription to Enable Payments'
                      : trialDaysRemaining > 0 
                        ? `${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'Day' : 'Days'} Remaining in Trial`
                        : 'Trial Ending Soon'}
                  </h3>
                  <p className="text-[#A9A3A3] text-sm mb-3">
                    {hasNoSubscription
                      ? 'To enable payments and start processing subscriptions, sign up for a RollOps subscription.'
                      : 'To enable payments, sign up for a subscription. Your billing settings are saved and will be automatically enabled once you have an active subscription.'}
                  </p>
                  <button
                    onClick={() => navigate('/subscriptions-plans/rollops')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] rounded-md text-sm font-semibold transition-colors"
                  >
                    <CreditCard className="w-4 h-4" />
                    Sign Up for Subscription
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alerts Section - Top Priority */}
        {!alertsLoading && alerts.length > 0 && (
          <div className="mb-8">
            <AlertCard alerts={alerts} maxAlerts={5} />
          </div>
        )}

        {/* Onboarding Dashboard - Shows draft student metrics */}
        <div className="mb-8">
          <OnboardingDashboard />
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Students"
            value={metricsLoading ? '...' : metrics?.totalStudents || 0}
            icon={Users}
            onClick={() => navigate('/students')}
            trend={
              metrics?.totalStudents
                ? {
                    value: 12, // TODO: Calculate actual trend
                    isPositive: true,
                    label: 'vs last month',
                  }
                : undefined
            }
          />
          <MetricCard
            title="Active Subscriptions"
            value={metricsLoading ? '...' : metrics?.activeSubscriptions || 0}
            icon={Package}
            onClick={() => navigate('/subscriptions-plans/gym')}
          />
          <MetricCard
            title="Monthly Revenue"
            value={
              metricsLoading
                ? '...'
                : `$${((metrics?.monthlyRevenue || 0) / 100).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
            }
            icon={DollarSign}
            trend={
              metrics?.monthlyRevenue
                ? {
                    value: 8, // TODO: Calculate actual trend
                    isPositive: true,
                    label: 'vs last month',
                  }
                : undefined
            }
          />
          <MetricCard
            title="Upcoming Classes"
            value={metricsLoading ? '...' : metrics?.upcomingClasses || 0}
            icon={Calendar}
            subtitle="Next 7 days"
            onClick={() => navigate('/schedule')}
          />
        </div>

        {/* Secondary Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <MetricCard
            title="Recent Attendance"
            value={metricsLoading ? '...' : metrics?.recentAttendance || 0}
            subtitle="Last 7 days"
            icon={TrendingUp}
          />
          <MetricCard
            title="Pending Payments"
            value={metricsLoading ? '...' : metrics?.pendingPayments || 0}
            icon={Clock}
            onClick={() => navigate('/payments')}
          />
          <MetricCard
            title="Retention Rate"
            value={
              metricsLoading
                ? '...'
                : `${((metrics?.retentionRate || 0) * 100).toFixed(1)}%`
            }
            subtitle="Monthly retention"
            icon={BarChart3}
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <QuickActionCard title="Quick Actions" actions={quickActions} columns={3} />
        </div>

        {/* Feed Preview - Coming Soon */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE] flex items-center gap-2">
              <Users className="w-5 h-5 text-rollops-accent" />
              Community Feed
            </CardTitle>
          </CardHeader>
          <CardContent className="!p-6">
            <div className="text-center py-12">
              <p className="text-white/80 mb-4">
                Your gym&apos;s social feed will appear here soon.
              </p>
              <p className="text-sm text-white/80">
                Share updates, celebrate achievements, and keep your community engaged.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
  );
}
